import { dashboardForAutoPersistence } from '@src/utils/presets/transientPreset';
import { setSaved } from '@store/tempDashboard/actions';
import { cloneDeep, get, isEqual, set } from 'lodash';
import { normalizeSavedConfig } from './normalizeSavedConfig';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { getSaveData, useUpdateSave } from './atoms';

type SavedDashboardMetaType = {
  id?: string;
  isSaved?: boolean;
  hasChanged?: boolean;
  isPublic?: boolean;
  creatorId?: number;
  name?: string;
  dashboardTypeId?: number;
};

export interface SavedDataInterface {
  meta: SavedDashboardMetaType;
  config: object;
  initialSavedData: any;
}

export type SavingStateObjectType = {
  meta?: SavedDashboardMetaType;
  config?: object;
  configPath?: string[];
  configValue?: any;
};

// This hook can accept the full or partial meta object
// and/or the full or partial config object.
// It also has the provision to accept a configPath as an array
// and a corresponding configValue for that path, to be able update some config item specifically
function useSaveSet({ pageKey, saveData }: { pageKey: string; saveData: SavingStateObjectType }) {
  const dispatch = useDispatch();
  const { updateData } = useUpdateSave({ pageKey });

  useEffect(() => {
    try {
      const previousSaveData = getSaveData({ pageKey });
      const currentSaveData = cloneDeep(previousSaveData);
      const { initialSavedData = {} } = currentSaveData;
      const id = get(currentSaveData, ['meta', 'id'], false);
      const wasChanged = get(currentSaveData, ['meta', 'hasChanged'], false);

      if (saveData.meta) {
        currentSaveData.meta = {
          ...currentSaveData.meta,
          ...saveData.meta,
        };
      }

      if (saveData.config) {
        currentSaveData.config = {
          ...currentSaveData.config,
          ...saveData.config,
        };
      }

      if (saveData.configPath && saveData.configValue !== undefined) {
        set(currentSaveData.config, saveData.configPath, saveData.configValue);
      }

      const nextConfig = normalizeSavedConfig(currentSaveData.config);
      const hasChanged = !isEqual(normalizeSavedConfig(initialSavedData.config), nextConfig);
      set(currentSaveData.meta, ['hasChanged'], hasChanged);
      // Auto-persist the original presentation during an import session; keep the
      // live configuration above available to explicit Save/Update/Share actions.
      // Hydration-only defaults must neither mark a legacy dashboard dirty nor write it.
      if (
        id &&
        (hasChanged !== wasChanged ||
          !isEqual(normalizeSavedConfig(previousSaveData.config), nextConfig))
      ) {
        const persistedConfig = dashboardForAutoPersistence(currentSaveData.config, pageKey);
        const persistedHasChanged = !isEqual(
          normalizeSavedConfig(initialSavedData.config),
          normalizeSavedConfig(persistedConfig),
        );
        dispatch(setSaved(id, cloneDeep(persistedConfig), persistedHasChanged));
      }
      if (!isEqual(previousSaveData, currentSaveData)) updateData(currentSaveData);
    } catch (e) {
      console.log('get/set save status', e);
    }
  }, [
    saveData.meta,
    saveData.config,
    saveData.configPath,
    saveData.configValue,
    dispatch,
    pageKey,
  ]);
}

export default useSaveSet;
